import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { adminApi } from '../../services/admin';
import type { RuleItem } from '../../types';

export default function AdminRules() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'rules', page, search, categoryFilter],
    queryFn: () => adminApi.getRules({
      page: String(page),
      search: search || undefined,
      category: categoryFilter || undefined,
    }),
  });

  const [mutationError, setMutationError] = useState<string | null>(null);

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.toggleRule(id, isActive),
    onSuccess: () => {
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'rules'] });
    },
    onError: () => {
      setMutationError('Failed to toggle rule. Please try again.');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => adminApi.duplicateRule(id),
    onSuccess: () => {
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'rules'] });
    },
    onError: () => {
      setMutationError('Failed to duplicate rule. Please try again.');
    },
  });

  const columns: Column<RuleItem>[] = [
    { key: 'name', header: 'Name' },
    { key: 'category', header: 'Category', render: (r) => (
      <span className="capitalize">{r.category}</span>
    )},
    { key: 'target_score', header: 'Target', render: (r) => (
      <span className="capitalize">{r.target_score}</span>
    )},
    { key: 'rule_type', header: 'Type', render: (r) => (
      <span className="capitalize">{r.rule_type}</span>
    )},
    { key: 'score_impact', header: 'Impact', render: (r) => (
      <span className={`font-semibold ${r.is_positive ? 'text-emerald-600' : 'text-red-600'}`}>
        {r.is_positive ? '+' : ''}{Number(r.score_impact).toFixed(1)}
      </span>
    )},
    { key: 'severity_level', header: 'Severity', render: (r) => {
      const variant = r.severity_level === 'critical' ? 'error' : r.severity_level === 'high' ? 'warning' : 'neutral';
      return <StatusBadge label={r.severity_level} variant={variant} />;
    }},
    { key: 'version', header: 'v' },
    { key: 'is_active', header: 'Active', render: (r) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleMutation.mutate({ id: r.id, isActive: !r.is_active });
        }}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          r.is_active ? 'bg-emerald-500' : 'bg-slate-300'
        }`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          r.is_active ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    )},
    { key: 'actions', header: '', render: (r) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          duplicateMutation.mutate(r.id);
        }}
        className="text-xs text-slate-400 hover:text-slate-600"
        title="Duplicate rule"
      >
        Copy
      </button>
    )},
  ];

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader
        title="Rules"
        description={`${data?.count ?? 0} scoring rules`}
        actions={
          <button
            onClick={() => navigate('/admin/rules/new')}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700"
          >
            Create Rule
          </button>
        }
      />
      {mutationError && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{mutationError}</p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          placeholder="Search rules..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full sm:w-80 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Categories</option>
          <option value="attendance">Attendance</option>
          <option value="patrol">Patrol</option>
          <option value="incident">Incident</option>
          <option value="report">Report Quality</option>
          <option value="feedback">Feedback</option>
          <option value="operational">Operational</option>
        </select>
      </div>
      <DataTable
        columns={columns}
        data={data?.results ?? []}
        keyExtractor={(r) => r.id}
        isLoading={isLoading}
        emptyTitle="No rules found"
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowClick={(r) => navigate(`/admin/rules/${r.id}`)}
      />
    </>
  );
}
